import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, List, Button, Input, Form, Space, message, Tag,
  Popconfirm, Select, Tooltip, Empty, Spin, Popover, Typography,
  Checkbox,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  BulbOutlined, SmileOutlined, PushpinOutlined, PushpinFilled,
  DownloadOutlined, UploadOutlined, FolderOutlined, FolderAddOutlined,
  CheckOutlined, CloseOutlined, InboxOutlined, RightOutlined, DownOutlined,
} from '@ant-design/icons';
import { useTheme } from '../contexts/ThemeContext';
import promptTemplatesService from '../services/promptTemplatesService';

const { TextArea } = Input;
const { Text } = Typography;

const commonIcons = [
  '📊', '🎯', '⚡', '💡', '🚀', '🔍', '📈', '📉',
  '⚠️', '✅', '❌', '🔔', '📝', '💬', '🎨', '🔧',
  '📦', '🌟', '🔥', '💻', '📱', '🌐', '🔐', '⏰',
  '📅', '📌', '🏆', '💰', '🎁', '🎉', '👥', '🤖',
];

// Per-folder iconic emoji used in the section header in place of the
// generic folder icon. Anything not listed falls back to FolderOutlined.
const FOLDER_ICONS = {
  'Workload Catalog': '📦',
  'Workforce Management': '👥',
  'Business & Product Catalog': '🏢',
  'Technical Catalog': '🛠️',
  'ITSM': '🎫',
  'Delivery Plan': '🗓️',
  'Source Code': '🌿',
  'Application': '🧩',
  'Cloud Infrastructure': '☁️',
  'Compute & OS': '🖥️',
  'Software Inventory': '💿',
  'Network': '🌐',
  'Cloud Security': '🛡️',
  'Observability': '🔭',
  'Architecture & Readiness Reviews': '🏛️',
  'AI Usage': '🤖',
};

const PromptLibraryDialog = ({ visible, onCancel, onSelectPrompt, onTemplateChange }) => {
  const { themeConfig } = useTheme();
  const [templates, setTemplates] = useState([]);
  const [folders, setFolders] = useState([]);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [addingFolder, setAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [iconPopoverVisible, setIconPopoverVisible] = useState(false);
  // Track which folder sections are expanded. Default = all collapsed.
  // While searching, we force-expand sections that have matches.
  const [expandedSections, setExpandedSections] = useState({});
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      loadTemplates();
      loadFolders();
    }
  }, [visible]);

  // Keyboard shortcut: "/" focuses the search input while the dialog is open.
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key !== '/' || isEditing) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      e.preventDefault();
      searchInputRef.current?.focus?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, isEditing]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await promptTemplatesService.listTemplates(null);
      setTemplates(data);
    } catch (error) {
      message.error('Failed to load prompt templates');
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      const data = await promptTemplatesService.listFolders(true);
      setFolders(data);
    } catch {
      // non-fatal
    }
  };

  // ── Folder handlers ──────────────────────────────────────────────────

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await promptTemplatesService.createFolder({ name: newFolderName.trim() });
      message.success('Folder created');
      setNewFolderName('');
      setAddingFolder(false);
      loadFolders();
    } catch (e) {
      message.error(e.message || 'Failed to create folder');
    }
  };

  const handleSaveFolder = async (folder) => {
    if (!editingFolderName.trim()) return;
    try {
      await promptTemplatesService.updateFolder(folder.id, { name: editingFolderName.trim() });
      message.success('Folder renamed');
      setEditingFolderId(null);
      loadFolders();
      loadTemplates();
      if (onTemplateChange) onTemplateChange();
    } catch (e) {
      message.error(e.message || 'Failed to rename folder');
    }
  };

  const handleDeleteFolder = async (folder) => {
    try {
      await promptTemplatesService.deleteFolder(folder.id);
      message.success('Folder deleted — prompts moved to No folder');
      loadFolders();
      loadTemplates();
      if (onTemplateChange) onTemplateChange();
    } catch (e) {
      message.error(e.message || 'Failed to delete folder');
    }
  };

  const handlePinFolder = async (folderName) => {
    try {
      const folderPrompts = templates.filter(t => t.folder === folderName);
      const allPinned = folderPrompts.every(t => t.show_in_homepage);
      for (const t of folderPrompts) {
        if (allPinned) {
          await promptTemplatesService.updateTemplate(t.id, { show_in_homepage: false });
        } else if (!t.show_in_homepage) {
          await promptTemplatesService.updateTemplate(t.id, { show_in_homepage: true });
        }
      }
      message.success(allPinned ? 'Folder unpinned from front page' : 'Folder pinned to front page');
      loadTemplates();
      if (onTemplateChange) onTemplateChange();
    } catch (error) {
      message.error('Failed to update');
    }
  };

  // ── Template handlers ────────────────────────────────────────────────

  const handleCreate = () => {
    setEditingTemplate(null);
    setIsEditing(true);
    form.resetFields();
    form.setFieldsValue({ icon: '💡', show_in_homepage: false, show_in_folder: true, folder: null });
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setIsEditing(true);
    form.setFieldsValue({
      label: template.label.replace(/^[\p{Emoji_Presentation}\p{Extended_Pictographic}]\s*/u, ''),
      prompt: template.prompt,
      icon: template.icon || '💡',
      show_in_homepage: template.show_in_homepage,
      folder: template.folder || null,
      show_in_folder: template.show_in_folder !== undefined ? template.show_in_folder : true,
    });
  };

  const handleSelectIcon = (icon) => {
    const currentLabel = form.getFieldValue('label') || '';
    const labelWithoutEmoji = currentLabel.replace(/^[\p{Emoji}\p{Emoji_Presentation}\p{Emoji_Modifier}\p{Emoji_Component}]+\s*/u, '');
    form.setFieldsValue({ icon, label: `${icon} ${labelWithoutEmoji}` });
    setIconPopoverVisible(false);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const icon = form.getFieldValue('icon') || '';
      const labelWithoutEmoji = values.label.replace(/^[\p{Emoji}\p{Emoji_Presentation}\p{Emoji_Modifier}\p{Emoji_Component}]+\s*/u, '');

      const templateData = {
        label: labelWithoutEmoji,
        prompt: values.prompt,
        icon,
        show_in_homepage: values.show_in_homepage || false,
        folder: values.folder || null,
        show_in_folder: values.show_in_folder !== undefined ? values.show_in_folder : true,
        display_order: 0,
      };

      if (editingTemplate) {
        await promptTemplatesService.updateTemplate(editingTemplate.id, templateData);
        message.success('Prompt template updated');
      } else {
        await promptTemplatesService.createTemplate(templateData);
        message.success('Prompt template created');
      }

      setIsEditing(false);
      setEditingTemplate(null);
      form.resetFields();
      loadTemplates();
      if (onTemplateChange) onTemplateChange();
    } catch (error) {
      if (error.errorFields) return;
      message.error('Failed to save prompt template');
    }
  };

  const handleDelete = async (templateId) => {
    try {
      await promptTemplatesService.deleteTemplate(templateId);
      message.success('Prompt template deleted');
      loadTemplates();
      if (onTemplateChange) onTemplateChange();
    } catch (error) {
      message.error('Failed to delete prompt template');
    }
  };

  const handleTogglePin = async (template) => {
    try {
      const newVal = !template.show_in_homepage;
      await promptTemplatesService.updateTemplate(template.id, { show_in_homepage: newVal });
      message.success(newVal ? 'Pinned to front page' : 'Unpinned from front page');
      loadTemplates();
      if (onTemplateChange) onTemplateChange();
    } catch (error) {
      message.error(error.message || 'Failed to update');
    }
  };

  const handleExport = async () => {
    try {
      const data = await promptTemplatesService.exportTemplates();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'prompt_templates.json';
      a.click();
      URL.revokeObjectURL(url);
      message.success(`Exported ${data.length} template(s)`);
    } catch (error) {
      message.error('Failed to export templates');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        message.error('Invalid JSON file');
        return;
      }
      if (!Array.isArray(parsed)) {
        message.error('Invalid format: expected a JSON array');
        return;
      }
      const result = await promptTemplatesService.importTemplates(parsed);
      message.success(`Import complete: ${result.created} created, ${result.updated} updated, ${result.skipped} skipped`);
      loadTemplates();
      if (onTemplateChange) onTemplateChange();
    } catch (error) {
      message.error('Failed to import templates');
    } finally {
      e.target.value = '';
    }
  };

  // ── Derived data ─────────────────────────────────────────────────────

  const isSearching = !!searchQuery.trim();
  const filteredTemplates = !isSearching
    ? templates
    : templates.filter(t => {
        const q = searchQuery.toLowerCase();
        return t.label.toLowerCase().includes(q)
          || t.prompt.toLowerCase().includes(q)
          || (t.folder || '').toLowerCase().includes(q);
      });

  const iconSelector = (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 4, maxWidth: 280 }}>
      {commonIcons.map((icon) => (
        <Button
          key={icon}
          type="text"
          size="large"
          onClick={() => handleSelectIcon(icon)}
          style={{ fontSize: 20, padding: 4, height: 36, width: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {icon}
        </Button>
      ))}
    </div>
  );

  // ── Build groupings for the flat sectioned layout ────────────────────
  // Each "section" = one folder (in folder.display_order) + a final
  // standalone (orphan) section. Search filters the prompts inside the
  // sections; sections with zero matches are hidden while searching.
  const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
  const sortedFolders = [...folders].sort((a, b) => {
    const da = a.display_order ?? 0; const db = b.display_order ?? 0;
    if (da !== db) return da - db;
    return collator.compare(a.name || '', b.name || '');
  });
  const sectionsAll = [
    ...sortedFolders.map(f => ({
      kind: 'folder',
      key: `folder-${f.id}`,
      folder: f,
      name: f.name,
      prompts: filteredTemplates.filter(t => t.folder === f.name),
    })),
    {
      kind: 'orphan',
      key: '__orphan__',
      folder: null,
      name: 'No folder',
      prompts: filteredTemplates.filter(t => !t.folder),
    },
  ];
  const sections = isSearching
    ? sectionsAll.filter(s => s.prompts.length > 0)
    : sectionsAll;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
          <Space>
            <BulbOutlined style={{ color: themeConfig.primary }} />
            <span>Prompt Library</span>
          </Space>
          <Space size={4}>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleExport}>Export</Button>
            <Button size="small" icon={<UploadOutlined />} onClick={() => fileInputRef.current.click()}>Import</Button>
            <input ref={fileInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleImport} />
          </Space>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={null}
      styles={{ body: { padding: 0 } }}
    >
      <style>{`
        .pl-section-header { display: flex; align-items: center; gap: 8px; padding: 8px 4px; }
        .pl-section-header .pl-section-actions { margin-left: auto; }
        .pl-prompt-body {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
          white-space: pre-wrap;
        }
        .pl-prompt-body--expanded { display: block; white-space: pre-wrap; }
      `}</style>
      {isEditing ? (
        /* ── Edit / Create form ───────────────────────────────────────── */
        <div style={{ padding: 16, height: 620, overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, color: themeConfig.textPrimary }}>
              {editingTemplate ? 'Edit Prompt' : 'Create New Prompt'}
            </h3>
            <Space>
              <Button onClick={() => setIsEditing(false)}>Cancel</Button>
              <Button type="primary" onClick={handleSave}>Save</Button>
            </Space>
          </div>
          <Form form={form} layout="vertical">
            <Form.Item
              name="label"
              label="Template Label"
              rules={[{ required: true, message: 'Please enter a label' }]}
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="e.g., 📊 Analyze Trends"
                size="large"
                autoFocus
                suffix={
                  <Popover
                    content={iconSelector}
                    title="Select an icon"
                    trigger="click"
                    open={iconPopoverVisible}
                    onOpenChange={setIconPopoverVisible}
                    placement="bottomRight"
                  >
                    <Button type="text" icon={<SmileOutlined />} style={{ color: themeConfig.textSecondary, fontSize: 16 }} title="Add icon" />
                  </Popover>
                }
              />
            </Form.Item>
            <Form.Item name="prompt" label="Prompt Text" rules={[{ required: true, message: 'Please enter the prompt text' }]}>
              <TextArea rows={10} placeholder="Enter the prompt that will be used when clicked..." />
            </Form.Item>
            <Form.Item name="folder" label="Folder">
              <Select
                allowClear
                showSearch
                placeholder="Select or type a folder name..."
                mode="tags"
                maxCount={1}
                tokenSeparators={[',']}
                options={folders.map(f => ({ value: f.name, label: f.name }))}
                onChange={(val) => form.setFieldsValue({ folder: Array.isArray(val) ? val[0] || null : val || null })}
              />
            </Form.Item>
            <Form.Item name="show_in_folder" valuePropName="checked">
              <Checkbox>Show within folder grouping (uncheck to display separately)</Checkbox>
            </Form.Item>
            <Form.Item name="show_in_homepage" valuePropName="checked">
              <Checkbox>Pin to front page (visible to all users on chat welcome screen)</Checkbox>
            </Form.Item>
          </Form>
        </div>
      ) : (
        /* ── Browse view: search + flat sectioned list ───────────────── */
        <div style={{ display: 'flex', flexDirection: 'column', height: 620 }}>
          {/* Toolbar: Search + New folder */}
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${themeConfig.border}`, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input.Search
              ref={searchInputRef}
              placeholder="Search prompts (press / to focus)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ flex: 1 }}
              allowClear
              size="middle"
            />
            {addingFolder ? (
              <Space.Compact>
                <Input
                  size="middle"
                  placeholder="Folder name"
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  onPressEnter={handleCreateFolder}
                  autoFocus
                  style={{ width: 180 }}
                />
                <Button size="middle" icon={<CheckOutlined />} type="primary" onClick={handleCreateFolder} />
                <Button size="middle" icon={<CloseOutlined />} onClick={() => setAddingFolder(false)} />
              </Space.Compact>
            ) : (
              <Button icon={<FolderAddOutlined />} onClick={() => { setAddingFolder(true); setNewFolderName(''); }}>
                New folder
              </Button>
            )}
          </div>

          {/* Sectioned list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 16px 16px' }}>
            <Spin spinning={loading}>
              {sections.length === 0 ? (
                <Empty description={isSearching ? 'No prompts match your search' : 'No prompts yet'} style={{ marginTop: 60 }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>Create a Prompt</Button>
                </Empty>
              ) : (
                sections.map(section => {
                  const isOrphan = section.kind === 'orphan';
                  const folder = section.folder;
                  const isRenaming = !isOrphan && editingFolderId === folder.id;
                  const allPinned = !isOrphan
                    && section.prompts.length > 0
                    && templates.filter(t => t.folder === folder.name).every(t => t.show_in_homepage);
                  // Collapsed by default; force open while searching so matches are visible.
                  const isExpanded = isSearching ? true : !!expandedSections[section.key];
                  const toggleExpand = () => setExpandedSections(prev => ({ ...prev, [section.key]: !prev[section.key] }));

                  return (
                    <div
                      key={section.key}
                      style={{
                        marginBottom: 12,
                        border: `1px solid ${themeConfig.border}`,
                        borderRadius: 8,
                        background: themeConfig.mode === 'dark' ? themeConfig.backgroundSecondary : '#fafafa',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Section header (click to toggle) */}
                      <div
                        className="pl-section-header"
                        onClick={(e) => {
                          // Avoid toggling when clicking interactive controls inside the header.
                          if (e.target.closest('button, input, .ant-popover, .ant-popconfirm')) return;
                          if (!isRenaming && !isSearching) toggleExpand();
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: isSearching || isRenaming ? 'default' : 'pointer',
                          background: isExpanded
                            ? (themeConfig.mode === 'dark' ? themeConfig.backgroundSecondary : '#f0f0f0')
                            : 'transparent',
                          borderBottom: isExpanded ? `1px solid ${themeConfig.border}` : 'none',
                        }}
                      >
                        {isRenaming ? (
                          <Space.Compact style={{ flex: 1 }}>
                            <Input
                              size="small"
                              value={editingFolderName}
                              onChange={e => setEditingFolderName(e.target.value)}
                              onPressEnter={() => handleSaveFolder(folder)}
                              autoFocus
                            />
                            <Button size="small" icon={<CheckOutlined />} type="primary" onClick={() => handleSaveFolder(folder)} />
                            <Button size="small" icon={<CloseOutlined />} onClick={() => setEditingFolderId(null)} />
                          </Space.Compact>
                        ) : (
                          <>
                            {!isSearching && (
                              isExpanded
                                ? <DownOutlined style={{ fontSize: 10, color: themeConfig.textSecondary }} />
                                : <RightOutlined style={{ fontSize: 10, color: themeConfig.textSecondary }} />
                            )}
                            {isOrphan ? (
                              <InboxOutlined style={{ color: themeConfig.textSecondary }} />
                            ) : FOLDER_ICONS[folder.name] ? (
                              <span style={{ fontSize: 18, lineHeight: 1 }}>{FOLDER_ICONS[folder.name]}</span>
                            ) : (
                              <FolderOutlined style={{ color: themeConfig.primary }} />
                            )}
                            <Text strong style={{ fontSize: 14 }}>{section.name}</Text>
                            <Tag style={{ fontSize: 11 }}>{section.prompts.length}</Tag>
                            {!isOrphan && (
                              <Space size={0} className="pl-section-actions">
                                <Tooltip title={allPinned ? 'Unpin folder from front page' : 'Pin folder to front page'}>
                                  <Button
                                    type="text" size="small"
                                    icon={allPinned ? <PushpinFilled style={{ fontSize: 12 }} /> : <PushpinOutlined style={{ fontSize: 12 }} />}
                                    style={{ color: allPinned ? '#1677ff' : themeConfig.textSecondary }}
                                    onClick={() => handlePinFolder(folder.name)}
                                  />
                                </Tooltip>
                                <Tooltip title="Rename folder">
                                  <Button
                                    type="text" size="small" icon={<EditOutlined style={{ fontSize: 12 }} />}
                                    onClick={() => { setEditingFolderId(folder.id); setEditingFolderName(folder.name); }}
                                  />
                                </Tooltip>
                                <Popconfirm
                                  title="Delete folder?"
                                  description="Prompts will move to No folder."
                                  onConfirm={() => handleDeleteFolder(folder)}
                                  okText="Delete" cancelText="Cancel"
                                >
                                  <Button type="text" size="small" danger icon={<DeleteOutlined style={{ fontSize: 12 }} />} />
                                </Popconfirm>
                              </Space>
                            )}
                            <Button
                              size="small"
                              type="link"
                              icon={<PlusOutlined />}
                              style={{ marginLeft: isOrphan ? 'auto' : 4 }}
                              onClick={() => {
                                // Auto-expand the section when adding into it.
                                if (!isExpanded && !isSearching) toggleExpand();
                                setEditingTemplate(null);
                                setIsEditing(true);
                                form.resetFields();
                                form.setFieldsValue({
                                  icon: '💡',
                                  show_in_homepage: false,
                                  show_in_folder: true,
                                  folder: isOrphan ? null : folder.name,
                                });
                              }}
                            >
                              New prompt
                            </Button>
                          </>
                        )}
                      </div>

                      {/* Prompts in this section (wrapped + collapsible) */}
                      {isExpanded && (
                        <div style={{ padding: '8px 12px' }}>
                          {section.prompts.length === 0 ? (
                            <div style={{ padding: '4px 4px', color: themeConfig.textSecondary, fontSize: 12, fontStyle: 'italic' }}>
                              {isSearching ? 'No matches' : 'No prompts yet'}
                            </div>
                          ) : (
                            <List
                              dataSource={section.prompts}
                              renderItem={(template) => (
                                <PromptItem
                                  key={template.id}
                                  template={template}
                                  themeConfig={themeConfig}
                                  onSelect={(prompt) => { if (onSelectPrompt) { onSelectPrompt(prompt); onCancel(); } }}
                                  onEdit={handleEdit}
                                  onDelete={handleDelete}
                                  onTogglePin={handleTogglePin}
                                />
                              )}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </Spin>
          </div>
        </div>
      )}
    </Modal>
  );
};


/* ── Prompt list item ─────────────────────────────────────────────────── */

function PromptItem({ template, themeConfig, onSelect, onEdit, onDelete, onTogglePin }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const lineCount = template.prompt.split('\n').length;
  // The CSS clamp shows the first ~4 lines; only offer expand when there's actually more.
  const hasMoreLines = lineCount > 4 || template.prompt.length > 240;

  return (
    <List.Item
      style={{
        border: `1px solid ${themeConfig.border}`,
        borderRadius: 8,
        marginBottom: 8,
        padding: '12px 16px',
        background: themeConfig.mode === 'dark' ? themeConfig.backgroundSecondary : 'white',
        cursor: 'pointer',
        transition: 'all 0.2s',
        alignItems: 'flex-start',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 2px 8px ${themeConfig.primary}20`; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
      onClick={() => onSelect(template.prompt)}
    >
      <List.Item.Meta
        title={
          <Space>
            {template.icon && <span style={{ fontSize: 16 }}>{template.icon}</span>}
            <Tooltip title="Click card to insert into chat" mouseEnterDelay={0.6}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{template.label}</span>
            </Tooltip>
            {template.show_in_homepage && (
              <Tag color="blue" style={{ fontSize: 10 }}>Pinned</Tag>
            )}
          </Space>
        }
        description={
          <div style={{ color: themeConfig.textSecondary, fontSize: 13, marginTop: 4 }}>
            <div className={isExpanded ? 'pl-prompt-body--expanded' : 'pl-prompt-body'}>
              {template.prompt}
            </div>
            {hasMoreLines && (
              <Button
                type="link"
                size="small"
                onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
                style={{ padding: 0, marginTop: 2, height: 'auto' }}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </Button>
            )}
          </div>
        }
      />
      <Space onClick={(e) => e.stopPropagation()} style={{ alignSelf: 'flex-start' }}>
        <Tooltip title={template.show_in_homepage ? 'Unpin from front page' : 'Pin to front page'}>
          <Button
            type="text" size="small"
            icon={template.show_in_homepage ? <PushpinFilled /> : <PushpinOutlined />}
            style={{ color: template.show_in_homepage ? '#1677ff' : themeConfig.textSecondary }}
            onClick={(e) => { e.stopPropagation(); onTogglePin(template); }}
          />
        </Tooltip>
        <Tooltip title="Edit">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); onEdit(template); }} />
        </Tooltip>
        <Popconfirm
          title="Delete this prompt?"
          onConfirm={(e) => { e.stopPropagation(); onDelete(template.id); }}
          okText="Delete"
          cancelText="Cancel"
          onClick={(e) => e.stopPropagation()}
        >
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
        </Popconfirm>
      </Space>
    </List.Item>
  );
}

export default PromptLibraryDialog;